import React, { useContext, useState } from 'react';
import { AiOutlineEdit, AiOutlineLock, AiOutlineMail, AiOutlineUser, AiOutlineTeam } from 'react-icons/ai';
import { MdVerified } from 'react-icons/md';
import { LoginContext } from '../../reactcontext/ReactContext';
import EditPasswordModal from './EditPasswordModal';

const MyProfile = () => {
  const { loginInfo } = useContext(LoginContext);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handlePasswordEdit = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
  };

  const getInitials = () => {
    return `${loginInfo.userDetails.firstName.charAt(0)}${loginInfo.userDetails.lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-blue-100 text-blue-700',
      user: 'bg-green-100 text-green-700',
      agent: 'bg-purple-100 text-purple-700',
    };
    return colors[role.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className='w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6 overflow-auto'>
      <div className='max-w-6xl mx-auto'>
        
        {/* Header Section */}
        <div className='mb-4'>
          <h1 className='text-2xl md:text-2xl font-bold text-gray-900 mb-0.5'>My Profile</h1>
          <p className='text-gray-600 text-xs'>Manage your account information and settings</p>
        </div>

        {/* Profile Header Card */}
        <div className='bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-xl p-5 md:p-6 mb-6 shadow-lg'>
          <div className='flex flex-col md:flex-row items-start md:items-center justify-between gap-5'>
            
            {/* Left Side - Avatar and Name */}
            <div className='flex items-center gap-4'>
              <div className='w-20 h-20 bg-white rounded-full flex items-center justify-center text-indigo-600 text-3xl font-bold shadow-md border-4 border-blue-100'>
                {getInitials()}
              </div>
              
              <div className='text-white'>
                <h2 className='text-2xl font-bold mb-1.5'>
                  {loginInfo.userDetails.firstName} {loginInfo.userDetails.lastName}
                </h2>
                <div className='flex items-center gap-2'>
                  <span className={`px-2.5 py-0.5 rounded-full font-semibold capitalize text-xs ${getRoleColor(loginInfo.userDetails.role)}`}>
                    {loginInfo.userDetails.role}
                  </span>
                  <span className='flex items-center gap-1 text-blue-100 text-xs'>
                    <MdVerified className='text-green-400 text-sm' />
                    Active
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side - Quick Stats */}
            <div className='grid grid-cols-2 gap-3 w-full md:w-auto'>
              <div className='bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 text-white border border-white border-opacity-20'>
                <p className='text-xs font-semibold text-blue-100 mb-0.5'>Status</p>
                <p className='text-sm font-bold'>Active</p>
              </div>
              <div className='bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-3 text-white border border-white border-opacity-20'>
                <p className='text-xs font-semibold text-blue-100 mb-0.5'>Member Since</p>
                <p className='text-sm font-bold'>2024</p>
              </div>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          
          {/* Main Content - Profile Details */}
          <div className='lg:col-span-2'>
            
            {/* Account Information */}
            <div className='bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100'>
              <div className='flex items-center gap-2.5 mb-6 pb-4 border-b-2 border-gray-100'>
                <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
                  <AiOutlineUser className='text-blue-600 text-lg' />
                </div>
                <h3 className='text-lg font-bold text-gray-900'>Account Information</h3>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                {/* First Name */}
                <div>
                  <label className='block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide'>First Name</label>
                  <div className='relative'>
                    <div className='absolute left-3 top-3 text-blue-500'>
                      <AiOutlineUser size={16} />
                    </div>
                    <input 
                      type='text'
                      value={loginInfo.userDetails.firstName}
                      disabled
                      className='w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:outline-none cursor-not-allowed'
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className='block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide'>Last Name</label>
                  <div className='relative'>
                    <div className='absolute left-3 top-3 text-blue-500'>
                      <AiOutlineUser size={16} />
                    </div>
                    <input 
                      type='text'
                      value={loginInfo.userDetails.lastName}
                      disabled
                      className='w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:outline-none cursor-not-allowed'
                    />
                  </div>
                </div>

                {/* Email */}
                <div className='md:col-span-2'>
                  <label className='block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide'>Email Address</label>
                  <div className='relative'>
                    <div className='absolute left-3 top-3 text-blue-500'>
                      <AiOutlineMail size={16} />
                    </div>
                    <input 
                      type='email'
                      value={loginInfo.userDetails.email}
                      disabled
                      className='w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:outline-none cursor-not-allowed'
                    />
                  </div>
                </div>

                {/* User ID */}
                <div>
                  <label className='block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide'>User ID</label>
                  <div className='bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-200'>
                    <p className='text-gray-800 font-mono font-medium text-xs'>{loginInfo.userDetails.id}</p>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className='block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide'>User Role</label>
                  <div className='relative'>
                    <div className='absolute left-3 top-3 text-blue-500'>
                      <AiOutlineTeam size={16} />
                    </div>
                    <input 
                      type='text'
                      value={loginInfo.userDetails.role}
                      disabled
                      className='w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:outline-none cursor-not-allowed capitalize'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className='bg-white rounded-xl shadow-md p-6 border border-gray-100'>
              <div className='flex items-center gap-2.5 mb-4 pb-4 border-b-2 border-gray-100'>
                <div className='w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center'>
                  <MdVerified className='text-green-600 text-lg' />
                </div>
                <h3 className='text-lg font-bold text-gray-900'>Account Status</h3>
              </div>

              <div className='space-y-3'>
                <div className='flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200'>
                  <span className='text-gray-700 font-medium text-sm'>Account Status</span>
                  <span className='px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold text-xs'>Active</span>
                </div>
                <div className='flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200'>
                  <span className='text-gray-700 font-medium text-sm'>Email Verified</span>
                  <span className='px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold text-xs flex items-center gap-1.5'>
                    <MdVerified size={14} /> Verified
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Security Section */}
          <div className='lg:col-span-1'>
            
            {/* Security Card */}
            <div className='bg-white rounded-xl shadow-md p-6 border border-gray-100 sticky top-6'>
              <div className='flex items-center gap-2.5 mb-4 pb-4 border-b-2 border-gray-100'>
                <div className='w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center'>
                  <AiOutlineLock className='text-red-600 text-lg' />
                </div>
                <h3 className='text-lg font-bold text-gray-900'>Security</h3>
              </div>

              <div className='space-y-3 mb-5'>
                <p className='text-gray-600 text-xs leading-relaxed'>
                  Keep your account secure by regularly updating your password.
                </p>
              </div>

              <button
                onClick={handlePasswordEdit}
                className='w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-2.5 px-5 rounded-lg transition duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-md text-sm'
              >
                <AiOutlineLock size={16} />
                Update Password
              </button>

              {/* Security Tips */}
              <div className='mt-6 pt-4 border-t border-gray-100'>
                <h4 className='font-semibold text-gray-900 mb-2 flex items-center gap-2 text-xs'>
                  <span className='w-1 h-1 bg-gray-900 rounded-full'></span>
                  Security Tips
                </h4>
                <ul className='space-y-1.5 text-xs text-gray-600'>
                  <li className='flex gap-2'>
                    <span className='text-blue-600 font-bold'>•</span>
                    <span>Use strong password</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-blue-600 font-bold'>•</span>
                    <span>Change password regularly</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-blue-600 font-bold'>•</span>
                    <span>Never share credentials</span>
                  </li>
                  <li className='flex gap-2'>
                    <span className='text-blue-600 font-bold'>•</span>
                    <span>Logout when done</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <EditPasswordModal
          user={{ id: loginInfo.userDetails.id }}
          onCancel={handlePasswordCancel}
        />
      )}
    </div>
  );
};

export default MyProfile;
